import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Box,
} from '@mui/material';
import { Link, useHistory } from "react-router-dom";
import { StyledTableRowBinary } from 'src/shared/Styles/Styles';
import { getClimatologyCodeTables, getStations, getStationTable } from 'src/apis/getWeatherCalls';
import { useStoreContext } from 'src/store/context';
import { useAddAlert, useAllStations } from 'src/store/actions';
import localforage from 'localforage';
import { alertMessages } from 'src/constants';
import { Station } from 'src/types';
import { ROUTE_PATHS } from 'src/routes';
import CustomTablePagination from 'src/shared/TablePagination/CustomTablePagination';
import { visuallyHidden } from '@mui/utils';
import { errorResponse, validateCharacters, ValidCharacters } from 'src/store/common';
import LoadingSpinner from 'src/shared/loading/loading';

interface Data {
  station_id: number
  station_name: string
  network_name: string
  state: string
  agency: string
}

/***
 * Following is used as an Enhanced header to filter the headers as needed
 * 
 */
interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'station_name',
    numeric: false,
    disablePadding: false,
    label: 'STATION NAME',
  },
  {
    id: 'station_id',
    numeric: false,
    disablePadding: false,
    label: 'STATION ID',
  },
  {
    id: 'network_name',
    numeric: false,
    disablePadding: false,
    label: 'NETWORK',
  },
  {
    id: 'state',
    numeric: false,
    disablePadding: false,
    label: 'STATE',
  },
  {
    id: 'agency',
    numeric: false,
    disablePadding: false,
    label: 'AGENCY OWNER',
  },
];

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}
/***
 * Used for styling the Cells for the inner tables
 * 
 */
function EnhancedTableHead(props: Readonly <EnhancedTableProps>) {
  const { order, orderBy, onRequestSort}=
    props;

    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => (
          (
            <TableCell
              className='headerParent'
              style={index ===0? {backgroundColor: '#d9d9d9', width: '30%'}: index===3?{backgroundColor: '#d9d9d9', width: '10%'}: {backgroundColor: '#d9d9d9', width: '20%'}}
              key={headCell.id}
              tabIndex={0}
              align={'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {/* Here we add the sort option + sort arrow */}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : <Box component="span" sx={visuallyHidden}></Box>}

                </TableSortLabel>

              }
            </TableCell>
          )
        ))}
      </TableRow>
    </TableHead>
  );
}

function Stations() {
  const allCodeObj =  useMemo(()=>({code_key: "All",code_table_id: -1, code_value:'All', type:'All'}),[]);

  const [networkCodes, setNetworkCodes] = useState<any[]>([allCodeObj]);
  const [stateCodes, setStateCodes] = useState<any[]>([allCodeObj]);
  const [agencyOwnerCodes, setAgencyOwnerCodes] = useState<any[]>([allCodeObj]);

  const [networkSelected, setNetworkSelected] = useState<string|undefined>(networkCodes[0].code_value);
  const [stateSelected, setStateSelected] = useState<string|undefined>(stateCodes[0].code_value);
  const [agencyOwnerSelected, setAgencyOwnerSelected] = useState<string|undefined>(agencyOwnerCodes[0].code_value);

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Data>('station_name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [state, dispatch] = useStoreContext();

  const [stationTable, setStationTable] = useState<Data[]>([]);
  const [failureCount, setFailureCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

	const addAlert = useAddAlert(dispatch);
  const allStoredStations = useAllStations(dispatch);
  const history = useHistory();

  const onStationSearch = (e:React.SyntheticEvent<Element, Event>, incomingVal: any) =>{
    if(incomingVal && state.all_stations?.length>1){
      const findStation = state.all_stations.find((station)=>station.search_name === incomingVal?.search_name);
      history.push(
        { pathname: `${ROUTE_PATHS.table}/fireDanger/${findStation?.station_id}`, state: {...findStation, from: 'search'} }
      )
    }
  }

  const handleRequestSort = (event:React.MouseEvent<unknown>, property:keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0)
    const requestSortUpdate={
      perPage: rowsPerPage,
      page: 0,
      sortBy: property,
      sortOrder: isAsc ? 'desc' : 'asc'
    }
    stationTableCallback(requestSortUpdate, failureCount);
  };

  /*call incase no station list is available*/
  const stationListCallback = useCallback(() =>{
		(async()=>{
			try{
				const varRequest = {
          returnAll: true,		
				};

				const response: any = await getStations(varRequest, addAlert, state.user);
        if (response) {
          const stations = response.stations.sort(function (a: any, b: any) {
            return a.search_name.localeCompare(b.search_name);
          })
          allStoredStations(stations);
        }
			}
			catch(error){
        console.log('error', error)
			}
		})();
	},[addAlert, allStoredStations, state?.user ]);

  /** Populate the drop downs for filtering the station table */
  const  climatologyCodesCallback = useCallback(() => {
    (async () => {
      try {
        const requestVars = {
          returnAll: true
        }
        const codeResponse: any = await getClimatologyCodeTables(requestVars);
        const networkCodes = codeResponse.networkId;
        const stateCodes = codeResponse.stateId;
        const agencyCodes = codeResponse.agency;

        networkCodes.unshift(allCodeObj);
        stateCodes.unshift(allCodeObj);
        agencyCodes.unshift(allCodeObj);

        setNetworkCodes(networkCodes);
        setStateCodes(stateCodes);
        setAgencyOwnerCodes(agencyCodes);
      } catch (error) {
        errorResponse(error, addAlert,alertMessages.retrievingStationData);
      }
    })();
  },[addAlert, allCodeObj])

  /** Make the api call and mapping for the station table */
  const stationTableCallback = useCallback((request: any, counter: number) => {
    setLoading(true);
    (async () => {
      try {
        const stationTableResponse: any = await getStationTable(request, addAlert, state.user);
        if(stationTableResponse.data.length > 0) {
          setStationTable(stationTableResponse.data);
          setTotalCount(stationTableResponse._metadata.total_count);
          setFailureCount(0)
        } else {
          setStationTable([]);
          setTotalCount(0);
          setFailureCount(counter+1)
        }
        setLoading(false);
        
      } catch(error) {
        console.log('error', error)
        setStationTable([]);
        setTotalCount(0);
        setFailureCount(counter+1);
        setLoading(false);
      }
    })();
  }, [addAlert, state?.user, setFailureCount, setLoading]);
	
  /** Check if the filter options are initialized */
  useEffect(()=>{
    if (state.all_stations?.length < 1) {
      localforage.getItem('allStations').then(async function (stations) {
        let allStations = stations as Station[];
        if (!allStations) {
          stationListCallback();
        }
        else {
          allStoredStations(allStations);
        }
      });
    }
    if(networkCodes?.length < 2 && stateCodes?.length < 2 && agencyOwnerCodes?.length < 2) {
      climatologyCodesCallback();
    }
  },[state.all_stations?.length, networkCodes?.length, stateCodes?.length, agencyOwnerCodes?.length,
    allStoredStations, stationListCallback, climatologyCodesCallback]);

  /** Populate the stations table */
  useEffect(() => {
    const request = {
      stationIds: null,
      networkName: networkSelected,
      stateId: stateSelected,
      agency: agencyOwnerSelected,
      perPage: rowsPerPage,
      page: page,
      sortBy: orderBy,
      sortOrder: order
    } 
    
    stationTableCallback(request, failureCount);
  }, [ failureCount, networkSelected, stateSelected, agencyOwnerSelected, order, orderBy, page, rowsPerPage, stationTableCallback])
  
  useEffect(()=>{
    if(failureCount>0 && failureCount<4){
      const request = {
        stationIds: null,
        networkName: networkSelected,
        stateId: stateSelected,
        agency: agencyOwnerSelected,
        perPage: rowsPerPage,
        page: page,
        sortBy: orderBy,
        sortOrder: order
      } 
      stationTableCallback(request, failureCount);
    }
  },[ failureCount, networkSelected, stateSelected, agencyOwnerSelected, order, orderBy, page, rowsPerPage, stationTableCallback])
  const getLinkToStation = (stationRow:any) => {
    return { pathname: `${ROUTE_PATHS.table}/fireDanger/${stationRow.station_id}`, state: {...stationRow} }
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    const updatePageCall = {
      perPage: rowsPerPage,
      page: newPage,
      sortBy: orderBy,
      sortOrder: order
    };
    stationTableCallback(updatePageCall, failureCount);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
    const updatePerPage = {
      perPage: Number.parseInt(event.target.value, 10),
      page: 0,
      sortBy: orderBy,
      sortOrder: order
    };
    stationTableCallback(updatePerPage, failureCount);
  };

  return (
    <div style={{overflowX: 'hidden', marginLeft: '.5rem', width: '98vw'}}>
      {/* Filters Section */}
      <Box sx={{ flexShrink: 0, mb: 2 }}>
        <div
          style={{
            marginLeft: '1.5%',
            paddingTop: '2%',
            maxWidth: '95vw',
          }}
        >
          <div>
            <div className="font-sans-md text-bold" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              {/* Search Station */}
              <div style={{ width: '37%'}}>
                <label
                  htmlFor="station"
                  className="text-bold" style={{ fontSize: '18px' }}
                >
                  Search Station
                </label>
                <div
                  className="searchStationsDownloadPage"
                >
                  {/* Search Icons */}
                  <i
                    className="fas fa-search"
                  />
                  <Autocomplete
                    id="station"
                    isOptionEqualToValue={(option,value)=>option?.search_name===value?.search_name}
                    options={state.all_stations? state.all_stations:[]}
                    getOptionLabel={(option)=>`${option.search_name}`}
                    filterOptions={(options, {inputValue})=>{
                        const search = inputValue.toLowerCase();
                        if(search.length<=2){
                            return options.filter((option)=>
                                option.state?.toLowerCase().includes(search) ||
                                option.station_id.toString().includes(search)

                            )
                        }
                        return options.filter((option)=>
                            option.station_name?.toLowerCase().includes(search) ||
                            option.network_name?.toLowerCase().includes(search) ||
                            option.state?.toLowerCase().includes(search)||
                            option.station_id.toString().includes(search)
                        )
                    }}  
                    onChange={(event, newValue) => onStationSearch(event, newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        aria-label={'Search Station...'}
                        placeholder="Search Station..."
                      />
                    )}
                  />
                </div>
              </div>
            {/* Network Filter */}
            <div style={{ width: '11%'}}>
                <label
                  htmlFor="Network"
                  className="text-bold" style={{ fontSize: '18px' }}
                >
                  Network
                </label>
                <Autocomplete
                  className='height-55'
                  disableClearable
                  id="Network"
                  options={networkCodes.map((option) => option.code_value)}
                  value={networkSelected}
                  
                  onChange={(event, newValue) =>{
                    setNetworkSelected(newValue)}
                  }
                  renderInput={(params) => (
                    <TextField 
                      onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                      data-testid={'Network'}
                      {...params} 
                      placeholder="Select State"                   
                      InputProps={{
                      ...params.InputProps,
                      type: 'search',
                    }} />
                  )}
                />
              </div>

              {/* State Filter */}
              <div style={{ width: '11%'}}>
                <label
                  htmlFor="state"
                  className="text-bold" style={{ fontSize: '18px' }}
                >
                  State
                </label>
                <Autocomplete
                  className='height-55'
                  id="state"
                  disableClearable
                  options={stateCodes.map((option) => option.code_value)}
                  value={stateSelected}
                  onChange={(event, newValue) => setStateSelected(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} 
                      placeholder="Select State"
                      InputProps={{
                        ...params.InputProps,
                        type: 'search',
                      }}
                    />
                  )}
                />
              </div>

              {/* AgencyOwner Filter */}
              <div style={{ width: '11%'}}>
                <label
                  htmlFor="AgencyOwner"
                  className="text-bold" style={{ fontSize: '18px' }}
                >
                  Agency Owner
                </label>
                <Autocomplete
                  className='height-55'
                  id="AgencyOwner"
                  options={agencyOwnerCodes.map((option) => option.code_value)}
                  disableClearable
                  value={agencyOwnerSelected}
                  onChange={(event, newValue) => setAgencyOwnerSelected(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} 
                      placeholder="Select State"
                      InputProps={{
                        ...params.InputProps,
                        type: 'search',
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>

      {/* Table Section */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', paddingTop: '1rem' }}>

        <TableContainer
          component={Paper}
          className='margin-top-1'
          style={{
            width: '95vw',
            maxWidth:'98vw',
          }}
        >
          {
            loading? (
                <div style={{ height: '200px' }}>
                  <LoadingSpinner />
                </div>
            ):(
                  <Table stickyHeader>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {stationTable.map((row, index) => (
                        <StyledTableRowBinary
                          key={`${row.station_name}-${index}`}
                          hover
                          style={{
                            textAlign: 'center',
                            cursor: 'pointer',
                            border: '2px solid transparent',
                            borderRadius: '4px',
                          }}
                        >
                          <TableCell className='text-align-override'>
                            <Link
                              to={getLinkToStation(row)}
                              color="primary"
                              style={{ fontWeight: 500 }}
                            >
                              {row.station_name}
                            </Link>
                          </TableCell>
                          <TableCell className='text-align-override'>{row.station_id}</TableCell>
                          <TableCell className='text-align-override'>{row.network_name}</TableCell>
                          <TableCell className='text-align-override'>{row.state}</TableCell>
                          <TableCell className='text-align-override'>{row.agency}</TableCell>

                        </StyledTableRowBinary>
                      ))}
                    </TableBody>
                  </Table>
            )
          }

          <CustomTablePagination
            rowsPerPageOptions={[10, 20, 30]}
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />   
        </TableContainer>
        </Box>
      </Box>
    </div>
  );
}

export default Stations;